import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, Gamepad2, Glasses, Wrench, Play, ChevronLeft, ChevronRight, Home, ExternalLink, Sparkles } from 'lucide-react';
import { heroData, servicesData, contactInfo } from '../data/mock';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_vr-techsolutions/artifacts/9vnskgnq_Gemini_Generated_Image_lf1inhlf1inhlf1i.png";

// Imágenes del carrusel para cada categoría
const carouselImages = [
  {
    id: 1,
    category: "PC Nuevas",
    image: "https://images.unsplash.com/photo-1587202372775-e229f172b9d7?w=1200",
    color: "violet"
  },
  {
    id: 2,
    category: "Consolas",
    image: "https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=1200",
    color: "pink"
  },
  {
    id: 3,
    category: "Electrónica",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200",
    color: "cyan"
  },
  {
    id: 4,
    category: "Impresión 3D",
    image: "https://images.unsplash.com/photo-1705475025559-ad8efdedc74f?w=1200",
    color: "orange"
  },
  {
    id: 5,
    category: "Estudio de Grabación",
    image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=1200",
    color: "red"
  },
  {
    id: 6,
    category: "Páginas Web",
    image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1200",
    color: "blue"
  },
  {
    id: 7,
    category: "Outlet",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200",
    color: "amber"
  },
  {
    id: 8,
    category: "Servicio Técnico",
    image: "https://images.unsplash.com/photo-1597872200969-2b65d56bd16b?w=1200",
    color: "emerald"
  }
];

const HeroCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % carouselImages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % carouselImages.length);
  };

  const currentImage = carouselImages[currentIndex];

  return (
    <div className="relative w-full max-w-5xl mx-auto rounded-2xl overflow-hidden group">
      {/* Main Image */}
      <div className="relative aspect-[16/7] overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.7 }}
            className="absolute inset-0"
          >
            <img
              src={currentImage.image}
              alt={currentImage.category}
              className="w-full h-full object-cover"
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60" />
          </motion.div>
        </AnimatePresence>

        {/* Category Label */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`label-${currentIndex}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="absolute bottom-6 left-6 right-6"
          >
            <span className={`inline-block px-4 py-2 rounded-full text-sm font-mono tracking-wider uppercase bg-${currentImage.color}-600/80 text-white backdrop-blur-sm`}
              style={{
                backgroundColor: currentImage.color === 'violet' ? 'rgba(124, 58, 237, 0.8)' :
                                currentImage.color === 'pink' ? 'rgba(236, 72, 153, 0.8)' :
                                currentImage.color === 'cyan' ? 'rgba(6, 182, 212, 0.8)' :
                                currentImage.color === 'orange' ? 'rgba(249, 115, 22, 0.8)' :
                                currentImage.color === 'red' ? 'rgba(239, 68, 68, 0.8)' :
                                currentImage.color === 'blue' ? 'rgba(59, 130, 246, 0.8)' :
                                currentImage.color === 'amber' ? 'rgba(245, 158, 11, 0.8)' :
                                'rgba(16, 185, 129, 0.8)'
              }}
            >
              {currentImage.category}
            </span>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Dots Indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
        {carouselImages.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className={`h-2 rounded-full transition-all duration-300 ${
              idx === currentIndex 
                ? 'w-8 bg-white' 
                : 'w-2 bg-white/40 hover:bg-white/60'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const Hero = ({ setActiveTab }) => {
  const iconMap = {
    service: Wrench,
    build: Cpu,
    sales: Gamepad2,
    vr: Glasses
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 12 }
    }
  };

  return (
    <section className="min-h-screen bg-black relative overflow-hidden pt-20">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-violet-800/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-violet-900/20 rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-violet-900/10 rounded-full" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-24">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center mb-16"
        >
          {/* Logo Image */}
          <motion.div variants={itemVariants} className="mb-6 flex flex-col items-center w-full">
            <img 
              src={LOGO_URL} 
              alt="SEBINOR Computación" 
              className="w-full max-w-2xl h-auto object-contain"
              style={{ 
                filter: 'drop-shadow(0 0 25px rgba(139, 92, 246, 0.4))',
                opacity: 0.75
              }}
            />
            <span 
              className="text-amber-400 text-2xl sm:text-3xl lg:text-4xl mt-4"
              style={{ fontFamily: "'Dancing Script', cursive" }}
            >
              25 años con vos
            </span>
          </motion.div>

          {/* Services Carousel */}
          <motion.div variants={itemVariants} className="mb-8">
            <HeroCarousel />
          </motion.div>

          {/* Tagline */}
          <motion.p 
            variants={itemVariants}
            className="text-gray-400 text-lg lg:text-xl max-w-2xl mx-auto mb-10"
          >
            {heroData.tagline}
          </motion.p>

          {/* Main Navigation Bar */}
          <motion.div 
            variants={itemVariants}
            className="w-full max-w-7xl mx-auto mb-10"
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
              <button
                onClick={() => setActiveTab('equipos')}
                className="group relative bg-gradient-to-br from-violet-600 to-violet-800 hover:from-violet-500 hover:to-violet-700 text-white rounded-xl px-2 py-4 text-center font-semibold text-xs lg:text-sm transition-all duration-300 shadow-lg shadow-violet-500/20 hover:shadow-violet-500/40 hover:scale-105"
              >
                <span className="relative z-10">PC Nuevas</span>
              </button>
              <button
                onClick={() => setActiveTab('equipos')}
                className="group relative bg-gradient-to-br from-pink-500 to-pink-700 hover:from-pink-400 hover:to-pink-600 text-white rounded-xl px-2 py-4 text-center font-semibold text-xs lg:text-sm transition-all duration-300 shadow-lg shadow-pink-500/20 hover:shadow-pink-500/40 hover:scale-105"
              >
                <span className="relative z-10">Consolas</span>
              </button>
              <button
                onClick={() => setActiveTab('equipos')}
                className="group relative bg-gradient-to-br from-cyan-500 to-cyan-700 hover:from-cyan-400 hover:to-cyan-600 text-white rounded-xl px-2 py-4 text-center font-semibold text-xs lg:text-sm transition-all duration-300 shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 hover:scale-105"
              >
                <span className="relative z-10">Electrónica</span>
              </button>
              <button
                onClick={() => setActiveTab('impresion3d')}
                className="group relative bg-gradient-to-br from-orange-500 to-orange-700 hover:from-orange-400 hover:to-orange-600 text-white rounded-xl px-2 py-4 text-center font-semibold text-xs lg:text-sm transition-all duration-300 shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 hover:scale-105"
              >
                <span className="relative z-10">Impresión 3D</span>
              </button>
              <button
                onClick={() => setActiveTab('estudio')}
                className="group relative bg-gradient-to-br from-red-500 to-red-700 hover:from-red-400 hover:to-red-600 text-white rounded-xl px-2 py-4 text-center font-semibold text-xs lg:text-sm transition-all duration-300 shadow-lg shadow-red-500/20 hover:shadow-red-500/40 hover:scale-105"
              >
                <span className="relative z-10">Estudio</span>
              </button>
              <button
                onClick={() => setActiveTab('web')}
                className="group relative bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-400 hover:to-blue-600 text-white rounded-xl px-2 py-4 text-center font-semibold text-xs lg:text-sm transition-all duration-300 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 hover:scale-105"
              >
                <span className="relative z-10">Páginas Web</span>
              </button>
              <button
                onClick={() => setActiveTab('outlet')}
                className="group relative bg-gradient-to-br from-amber-500 to-amber-700 hover:from-amber-400 hover:to-amber-600 text-white rounded-xl px-2 py-4 text-center font-semibold text-xs lg:text-sm transition-all duration-300 shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-105"
              >
                <span className="relative z-10">Outlet</span>
              </button>
              <button
                onClick={() => setActiveTab('servicio')}
                className="group relative bg-gradient-to-br from-emerald-500 to-emerald-700 hover:from-emerald-400 hover:to-emerald-600 text-white rounded-xl px-2 py-4 text-center font-semibold text-xs lg:text-sm transition-all duration-300 shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 hover:scale-105"
              >
                <span className="relative z-10">Servicio Técnico</span>
              </button>
            </div>
          </motion.div>

          {/* Tienda Electrónica del Hogar - Botón destacado */}
          <motion.div
            variants={itemVariants}
            className="w-full max-w-4xl mx-auto mb-10"
            data-testid="home-electronics-store-section"
          >
            <motion.a
              href="https://poetic-souffle-0c6916.netlify.app/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-electronics-store-btn"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="group relative block w-full rounded-2xl overflow-hidden shadow-2xl shadow-fuchsia-500/30 hover:shadow-fuchsia-400/50 transition-all duration-500"
            >
              {/* Animated gradient background */}
              <div className="absolute inset-0 bg-gradient-to-r from-fuchsia-600 via-violet-600 to-indigo-600 animate-pulse-slow" />
              <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/0 via-violet-500/20 to-indigo-500/0 group-hover:from-fuchsia-500/30 group-hover:to-indigo-500/30 transition-all duration-500" />

              {/* Decorative blurred circles */}
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-fuchsia-400/30 rounded-full blur-3xl group-hover:bg-fuchsia-400/50 transition-all duration-500" />
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-indigo-400/30 rounded-full blur-3xl group-hover:bg-indigo-400/50 transition-all duration-500" />

              {/* Sparkle accent */}
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                className="absolute top-4 right-4 text-white/40"
              >
                <Sparkles className="w-6 h-6" />
              </motion.div>

              {/* Content */}
              <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-4 px-6 sm:px-10 py-8 sm:py-10">
                <div className="flex items-center gap-5">
                  <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/30 group-hover:bg-white/25 transition-all duration-300">
                    <Home className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-white/80 text-xs sm:text-sm font-mono uppercase tracking-widest mb-1">
                      Visitá nuestra tienda
                    </p>
                    <h3 className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                      Electrónica del Hogar
                    </h3>
                    <p className="text-white/90 text-sm sm:text-base mt-1">
                      Heladeras, lavarropas, cocinas, microondas y mucho más
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/40 rounded-full px-6 py-3 transition-all duration-300 group-hover:translate-x-1">
                  <span className="text-white font-semibold text-sm sm:text-base whitespace-nowrap">
                    Ir a la tienda
                  </span>
                  <ExternalLink className="w-5 h-5 text-white" />
                </div>
              </div>
            </motion.a>
          </motion.div>

          {/* TikTok Button - Ver trabajos realizados */}
          <motion.div 
            variants={itemVariants}
            className="flex flex-col items-center mb-8"
          >
            <p className="text-gray-400 text-sm mb-3">Mirá algunos de nuestros trabajos realizados</p>
            <motion.a
              href={contactInfo.social.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center gap-3 bg-black border-2 border-white/20 hover:border-pink-500 rounded-full px-8 py-4 transition-all duration-300 hover:shadow-lg hover:shadow-pink-500/20"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              {/* TikTok Icon */}
              <svg 
                className="w-6 h-6 text-white group-hover:text-pink-400 transition-colors" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
              <span className="text-white font-semibold text-base group-hover:text-pink-400 transition-colors">
                Ver en TikTok
              </span>
              <Play className="w-5 h-5 text-pink-400 fill-pink-400" />
            </motion.a>
          </motion.div>

          {/* Google Review Button */}
          <motion.div 
            variants={itemVariants}
            className="flex flex-col items-center"
          >
            <motion.a
              href="https://g.page/r/Cd37D8GCOZ4DEAE/review"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center gap-3 bg-white hover:bg-gray-100 text-gray-800 rounded-full px-8 py-4 transition-all duration-300 shadow-lg hover:shadow-xl"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Google Icon */}
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="font-semibold text-base">
                Dejá tu reseña
              </span>
              {/* Star Icon */}
              <svg className="w-5 h-5 text-amber-400 fill-amber-400" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </motion.a>
            
            {/* Rating Badge */}
            <div className="mt-4 flex items-center gap-2">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg 
                    key={star} 
                    className={`w-5 h-5 ${star <= 4 ? 'text-amber-400 fill-amber-400' : 'text-amber-400 fill-amber-400/50'}`} 
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
              </div>
              <span className="text-amber-400 font-bold text-lg">4.7</span>
            </div>
            <p 
              className="text-amber-400 text-lg mt-2"
              style={{ fontFamily: "'Dancing Script', cursive" }}
            >
              ¡Gracias por el apoyo!
            </p>
          </motion.div>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {servicesData.map((service) => {
            const IconComponent = iconMap[service.category];
            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                className="group bg-white/5 border border-violet-900/30 rounded-xl p-6 cursor-pointer hover:bg-violet-600/10 hover:border-violet-500/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg bg-violet-600/20 flex items-center justify-center mb-4 group-hover:bg-violet-600/30 transition-colors">
                  <IconComponent className="w-6 h-6 text-violet-400" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{service.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
